import axios from "axios";

const API = process.env.REACT_APP_API_URL;

// 🔵 DiagnosisAnnotation

export async function createDiagnosisAnnotation(
  token: string,
  body: {
    userId: string;
    caseId: string;
    diagnosis: string;
    source: "AI" | "human";
    notes?: string;
    confidence?: number;
  }
) {
  const res = await axios.post(`${API}/annotations/diagnosis`, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function listDiagnosisAnnotations(
  token: string,
  caseId: string,
  source?: "AI" | "human"
) {
  const params = source ? { source } : {};
  const res = await axios.get(`${API}/annotations/diagnosis/${caseId}`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return res.data as {
    id: string;
    diagnosis: string;
    source: "AI" | "human";
    notes: string;
    confidence: number;
    userId: string;
  }[];
}

export async function updateDiagnosisAnnotation(
  token: string,
  id: string,
  body: {
    diagnosis?: string;
    source?: "AI" | "human";
    notes?: string;
    confidence?: number;
  }
) {
  const res = await axios.patch(`${API}/annotations/diagnosis/${id}`, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function deleteDiagnosisAnnotation(token: string, id: string) {
  const res = await axios.delete(`${API}/annotations/diagnosis/${id}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

// 🔵 DetectionResult

export async function createDetection(
  token: string,
  body: {
    userId: string;
    caseId: string;
    imageFile: string;
    label: string;
    confidence: number;
    x_min: number;
    y_min: number;
    x_max: number;
    y_max: number;
    detectionIndex?: number;
  }
) {
  const res = await axios.post(`${API}/annotations/detection`, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function batchCreateDetections(
  token: string,
  body: {
    userId: string;
    caseId: string;
    imageFile: string;
    detections: {
      userId: string;
      caseId: string;
      imageFile: string;
      label: string;
      confidence: number;
      x_min: number;
      y_min: number;
      x_max: number;
      y_max: number;
      detectionIndex?: number;
    }[];
  }
) {
  const res = await axios.post(`${API}/annotations/detection/batch`, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function runYoloDetection(
  token: string,
  body: {
    caseId: string;
    userId: string;
    imagePath: string;
    saveTxt?: boolean;
  }
) {
  const res = await axios.post(`${API}/annotations/detection/yolo`, body, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}

export async function listDetections(
  token: string,
  caseId: string,
  label?: string
) {
  if (!token) return [];
  const params = label ? { label } : {};
  try {
    const res = await axios.get(`${API}/annotations/detection/${caseId}`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    console.log("Detection response data:", res.data);
    
    // Handle different possible response formats:
    // 1. Array directly
    if (Array.isArray(res.data)) {
      return res.data;
    }
    // 2. { detections: [...] } format
    else if (res.data && res.data.detections && Array.isArray(res.data.detections)) {
      return res.data.detections;
    }
    // 3. Other object format - try to extract array
    else if (res.data && typeof res.data === 'object') {
      // Look for any array property that might contain our detections
      for (const key in res.data) {
        if (Array.isArray(res.data[key])) {
          console.log(`Found array in response at key: ${key}`);
          return res.data[key];
        }
      }
    }
    
    // If we get here, we couldn't find an array
    console.warn("No detection array found in response", res.data);
    return [];
  } catch (e: any) {
    if (e.response?.status === 403) {
      console.warn("Forbidden (403) – check token / permissions");
    } else {
      console.error("Error fetching detections:", e);
    }
    return [];
  }
}


export async function updateDetection(
  token: string,
  caseId: string,
  body: Partial<{
    label: string;
    confidence: number;
    x_min: number;
    y_min: number;
    x_max: number;
    y_max: number;
    detectionIndex: number;
  }>
) {
  console.log(`Updating detection for case ${caseId} with data:`, body);
  try {
    const res = await axios.patch(`${API}/annotations/detection/${caseId}`, body, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log("Update detection response:", res.data);
    return res.data;
  } catch (error: any) {
    console.error("Error updating detection:", error.response?.data || error.message);
    throw error;
  }
}

export async function deleteDetection(token: string, caseId: string) {
  const res = await axios.delete(`${API}/annotations/detection/${caseId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}